import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Phone as PhoneIcon, Mail, MapPin, Send, CheckCircle, Instagram, Building2 } from 'lucide-react';

const Contact = () => {
  const [formStatus, setFormStatus] = useState('idle');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('submitting');
    
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setFormStatus('success');
        e.target.reset();
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  return (
    <div className="bg-white">
      <Helmet>
        <title>Kontakt — Toftes Hundesalon</title>
        <meta name="description" content="Kontakt Toftes Hundesalon for tidsbestilling eller spørgsmål. Vi er placeret i Tranbjerg, tæt på Aarhus." />
        <meta property="og:title" content="Kontakt — Toftes Hundesalon" />
      </Helmet>

      {/* Header */}
      <section className="py-24 px-4 bg-stone-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-sm uppercase tracking-[0.4em] font-bold text-stone-700 mb-6">Kontakt</h2>
          <h1 className="!text-[2rem] md:text-6xl font-serif italic text-stone-900 mb-10 leading-tight">Kontakt os</h1>
          <p className="text-xl text-gray-600 italic">
            Jeg glæder mig til at høre fra dig og din hund.
          </p>
        </div>
      </section>

      <section className="py-24 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-24">
            {/* Contact Info */}
            <div>
              <h2 className="text-sm uppercase tracking-[0.4em] font-bold text-stone-700 mb-12">Information</h2>
              
              <div className="space-y-12 mb-16">
                <div className="flex items-start gap-6">
                  <MapPin className="w-6 h-6 text-stone-700 shrink-0" />
                  <div>
                    <h3 className="text-xs uppercase tracking-widest font-bold text-stone-900 mb-3">Adresse</h3>
                    <p className="text-gray-600 leading-relaxed italic">
                      Toftes Hundesalon v/ Helene Tofte<br />
                      Christian x’s vej 264<br />
                      8310 Tranbjerg J, Slet
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-6">
                  <PhoneIcon className="w-6 h-6 text-stone-700 shrink-0" />
                  <div>
                    <h3 className="text-xs uppercase tracking-widest font-bold text-stone-900 mb-3">Telefon</h3>
                    <a href="tel:+4528550499" className="text-gray-600 hover:text-stone-700 italic text-lg transition-colors">28 55 04 99</a>
                  </div>
                </div>

                <div className="flex items-start gap-6">
                  <Mail className="w-6 h-6 text-stone-700 shrink-0" />
                  <div>
                    <h3 className="text-xs uppercase tracking-widest font-bold text-stone-900 mb-3">E-mail</h3>
                    <a href="mailto:tofteshundesalon@gmail.com" className="text-gray-600 hover:text-stone-700 italic text-lg transition-colors">tofteshundesalon@gmail.com</a>
                  </div>
                </div>

                <div className="flex items-start gap-6">
                  <Building2 className="w-6 h-6 text-stone-700 shrink-0" />
                  <div>
                    <h3 className="text-xs uppercase tracking-widest font-bold text-stone-900 mb-3">CVR</h3>
                    <p className="text-gray-600 italic text-lg">3195 2697</p>
                  </div>
                </div>

                <div className="flex items-start gap-6">
                  <Instagram className="w-6 h-6 text-stone-700 shrink-0" />
                  <div>
                    <h3 className="text-xs uppercase tracking-widest font-bold text-stone-900 mb-3">Instagram</h3>
                    <a href="https://www.instagram.com/toftes_hundesalon" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-stone-700 italic text-lg transition-colors">@toftes_hundesalon</a>
                  </div>
                </div>
              </div>

              <div className="bg-stone-50 p-12 border-l-4 border-stone-700">
                <p className="text-gray-600 italic mb-6 leading-relaxed">
                  “Salonen ligger kun 10-15 minutters kørsel fra Aarhus midtby. Parkering kan nemt ske i indkørslen eller ved Jem&Fix på den anden side af viaden.”
                </p>
                <p className="text-xs uppercase tracking-widest font-bold text-stone-900">— Helene</p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-stone-50 p-12 md:p-16 shadow-sm">
              <h2 className="text-sm uppercase tracking-[0.4em] font-bold text-stone-700 mb-12">Send en besked</h2>
              
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Honeypot */}
                <div className="sr-only absolute" aria-hidden="true">
                  <label htmlFor="website_url">Website URL</label>
                  <input type="text" id="website_url" name="website_url" tabIndex="-1" autoComplete="off" />
                </div>

                <div className="grid grid-cols-1 gap-8">
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest font-bold text-stone-900 mb-3">Navn</label>
                    <input 
                      type="text" 
                      name="name" 
                      required 
                      className="w-full px-0 py-3 bg-transparent border-b border-stone-300 focus:border-stone-700 outline-none transition-all italic"
                      placeholder="Dit navn"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest font-bold text-stone-900 mb-3">E-mail</label>
                    <input 
                      type="email" 
                      name="email" 
                      required 
                      className="w-full px-0 py-3 bg-transparent border-b border-stone-300 focus:border-stone-700 outline-none transition-all italic"
                      placeholder="din@mail.dk"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] uppercase tracking-widest font-bold text-stone-900 mb-3">Besked</label>
                  <textarea 
                    name="message" 
                    required 
                    rows="4" 
                    className="w-full px-0 py-3 bg-transparent border-b border-stone-300 focus:border-stone-700 outline-none transition-all resize-none italic"
                    placeholder="Skriv din besked her..."
                  ></textarea>
                </div>

                <button 
                  type="submit" 
                  disabled={formStatus === 'submitting'}
                  className="w-full bg-stone-800 text-white px-8 py-5 rounded-none text-xs uppercase tracking-widest font-bold hover:bg-stone-900 transition-all disabled:opacity-50 flex items-center justify-center gap-3"
                >
                  {formStatus === 'submitting' ? 'Sender...' : (
                    <>
                      Send besked <Send className="w-4 h-4" />
                    </>
                  )}
                </button>

                {formStatus === 'success' && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-6 bg-green-50 text-green-700 text-sm italic flex items-center gap-4"
                  >
                    <CheckCircle className="w-5 h-5" />
                    Din besked er sendt! Jeg vender tilbage hurtigst muligt.
                  </motion.div>
                )}

                {formStatus === 'error' && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-6 bg-red-50 text-red-700 text-sm italic"
                  >
                    Der skete en fejl. Prøv venligst igen eller ring til mig.
                  </motion.div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map/Image Section */}
      <section className="py-24 px-4 bg-stone-50">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
          <div className="h-64 md:h-[500px] overflow-hidden shadow-xl">
            <img 
              src="https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/7c34ca47-e1b9-447d-905e-aae987868726.png" 
              alt="Kort over placering" 
              className="w-full h-full object-cover transition-all duration-1000"
            />
          </div>
          <div className="h-64 md:h-[500px] overflow-hidden shadow-xl">
            <img 
              src="https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/50a07f3a-c4e7-4fe8-9d58-56a268eb197d.png" 
              alt="Salonen indvendig" 
              className="w-full h-full object-cover transition-all duration-1000"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
